import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Modal } from "@core/enums/modal";
import { Permission } from '@core/enums/permissions';
import { States } from "@core/enums/states";
import { UploadTypes } from "@core/enums/upload-types";
import { AuthService } from "@core/services/auth.service";
import { LookupService } from "@core/services/lookup.service";
import { PermissionService } from '@core/services/permission.service';
import { StateService } from "@core/services/state.service";
import { ToastService } from "@core/services/toast.service";
import { UploadService } from "@core/services/upload.service";
import { IUploadValidateHeaders } from "@core/interfaces/iupload-center";
import { getHeaderValidationErrorMessage } from "@core/services/helper.service";
import { ConfirmationModalComponent } from "@shared/components/confirmation-modal/confirmation-modal.component";
import { switchMap, take, tap } from "rxjs";
import { ISchoolYear } from "@core/interfaces/iratingperiod";
import { RatingViewVersionEnum } from "@core/enums/rating-view-version.enum";
import { TranslocoService } from "@jsverse/transloco";

@Component({
    selector: 'drdp-upload-center',
    templateUrl: './upload-center.component.html',
    styleUrls: ['./upload-center.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UploadCenterComponent implements OnInit {

  @ViewChild('moreChildFileInput') moreChildFileInputRef!: ElementRef;
  @ViewChild('moreChild2025FileInput') moreChild2025FileInputRef!: ElementRef;
  isUploading: boolean = false;
  isCalifornia: boolean = this.authService.getUserStateId() == States.california;
  UploadTypes = UploadTypes;
  enforce2025?: boolean;
  overrideRatings: boolean = this.permissionService.checkPermission(Permission.OverrideLockedRatings);

  constructor(private uploadService: UploadService, private router: Router, private toast: ToastService,
    private lookupService: LookupService, private stateService: StateService,
    public modal: MatDialog, private authService: AuthService, private permissionService: PermissionService,
    private translocoService: TranslocoService) { }

  ngOnInit(): void {
    this.checkStateConfiguration();
    window.addEventListener("dragover", e => {
      e && e.preventDefault();
    }, false);
    window.addEventListener("drop", e => {
      e && e.preventDefault();
    }, false);
  }

  checkStateConfiguration(): void {
    const stateId = this.authService.getUserStateId();
    var schoolYear: ISchoolYear; 
    this.lookupService.getCurrentSchoolYear().pipe(
      tap(sy => schoolYear = sy),
      switchMap(() => this.stateService.getStatesById(stateId)),
      take(1)
    ).subscribe(res => {
      this.enforce2025 = schoolYear.enforceStateConfig && res.ratingViewVersionId === RatingViewVersionEnum.VERSION_2025;
    });
  }

  onFileChange(event: any) {
    if (!this.isFileAllowed(event.target.files)) {
      this.toast.error(this.translocoService.translate('upload.uploadCenter.invalidFileToast'));
      return;
    }

    const file = event.target.files[0];
    this.uploadService.setUploadFile(file);
    this.router.navigate(['upload-center/child']);
  }

  onFileChangeClass(event: any) {
    if (!this.isFileAllowed(event.target.files)) {
      this.toast.error(this.translocoService.translate('upload.uploadCenter.invalidFileToast'));
      return;
    }

    const file = event.target.files[0];
    this.uploadService.setUploadFile(file);
    this.router.navigate(['upload-center/class']);
  }


  onFileChangeUser(event: any) {
    if (!this.isFileAllowed(event.target.files)) {
      this.toast.error(this.translocoService.translate('upload.uploadCenter.invalidFileToast'));
      return;
    }

    const file = event.target.files[0];
    this.uploadService.setUploadFile(file);
    this.router.navigate(['upload-center/user']);
  }

  onFileChangeMoreChildren(event: any, uploadType: number) {

    if (this.isUploading) {
      this.openFailModal();
      return;
    }

    if (!this.isFileAllowed(event.target.files)) {
      this.toast.error(this.translocoService.translate('upload.uploadCenter.invalidFileToast'));
      return;
    }
    this.isUploading = true;
    const file = event.target.files[0];
    this.uploadService.saveMoreChild(file, uploadType).pipe(take(1)).subscribe({
      next: (res: IUploadValidateHeaders) => this.handleUploadResult(res, uploadType),
      error: (error: any) => {
        this.isUploading = false;
        this.genericModal();
      }
    });
  }

  uploadConsoleChildDropHandler(event: any, uploadType: number) {
    event.preventDefault();
    event.target.classList.remove('drag-over');

    const files = event.dataTransfer.files;
    if (this.isUploading) {
      this.openFailModal();
      return;
    }

    if (!this.isFileAllowed(files)) {
      this.toast.error(this.translocoService.translate('upload.uploadCenter.invalidFileToast'));
      return;
    }
    this.isUploading = true;

    this.uploadService.saveMoreChild(files[0], uploadType).pipe(take(1)).subscribe({
      next: (res: IUploadValidateHeaders) => this.handleUploadResult(res, uploadType),
      error: (error: any) => {
        this.isUploading = false;
        this.genericModal();
      }
    })
  }

  uploadChildDropHandler(event: any) {
    event.preventDefault();
    event.target.classList.remove('drag-over');

    const files = event.dataTransfer.files;

    if (!this.isFileAllowed(files)) {
      this.toast.error(this.translocoService.translate('upload.uploadCenter.invalidFileToast'));
      return;
    }

    this.uploadService.setUploadFile(files[0]);
    this.router.navigate(['upload-center/child']);
  }

  uploadClassDropHandler(event: any) {
    event.preventDefault();
    event.target.classList.remove('drag-over');

    const files = event.dataTransfer.files;

    if (!this.isFileAllowed(files)) {
      this.toast.error(this.translocoService.translate('upload.uploadCenter.invalidFileToast'));
      return;
    }

    this.uploadService.setUploadFile(files[0]);
    this.router.navigate(['upload-center/class']);
  }

  uploadUserDropHandler(event: any) {
    event.preventDefault();
    event.target.classList.remove('drag-over');

    const files = event.dataTransfer.files;

    if (!this.isFileAllowed(files)) {
      this.toast.error(this.translocoService.translate('upload.uploadCenter.invalidFileToast'));
      return;
    }

    this.uploadService.setUploadFile(files[0]);
    this.router.navigate(['upload-center/user']);
  }

  isFileAllowed(files: any): boolean {
    for (let i = 0; i < files.length; i++) {
      if (!files[i].type.match('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') && files[i].name.split('.').pop().toLowerCase() !== 'xlsx') {
        return false;
      }
    }
    return true;
  }

  handleUploadResult(res: IUploadValidateHeaders, uploadType: number): void {
    this.isUploading = false;
    if (res.isValidUpload) {
      this.openSuccessModal();
    } else if (res.missingColumns && res.missingColumns.length || res.invalidColumns && res.invalidColumns.length) {
      this.showHeaderValidationError(res);
    } else {
      this.genericModal();
    }

    const fileInputMap: Record<number, ElementRef> = {
      [UploadTypes.moreThan201Student]: this.moreChildFileInputRef,
      [UploadTypes.student2025]: this.moreChild2025FileInputRef
    };

    const fileInputRef = fileInputMap[uploadType] || this.moreChildFileInputRef;
    fileInputRef.nativeElement.value = '';
}

  openSuccessModal(): void {
    var event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('upload.uploadCenter.successTitle'),
      message: this.translocoService.translate('upload.uploadCenter.successMessage'),
      primaryBtnClass: 'blue',
      showCancel: false,
      primaryBtnText: Modal.OK
    };
    const modalRef = this.modal.open(ConfirmationModalComponent, { data: event ? event : undefined });
    modalRef.afterClosed().subscribe();
  }

  openFailModal(): void {
    var event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('upload.uploadCenter.failedTitle'),
      message: this.translocoService.translate('upload.uploadCenter.pendingUploadMessage'),
      primaryBtnClass: 'blue',
      showCancel: false,
      primaryBtnText: Modal.OK
    };
    const modalRef = this.modal.open(ConfirmationModalComponent, { data: event ? event : undefined });
    modalRef.afterClosed().subscribe();
  }

  genericModal(): void{
    var event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('upload.uploadCenter.failedTitle'),
      message: this.translocoService.translate('upload.uploadCenter.genericFailureMessage'),
      primaryBtnClass: 'blue',
      showCancel: false,
      primaryBtnText: Modal.OK
    };
    const modalRef = this.modal.open(ConfirmationModalComponent, { data: event ? event : undefined });
    modalRef.afterClosed().subscribe();
  }

  private showHeaderValidationError(validationResult: IUploadValidateHeaders): void {
    const message = getHeaderValidationErrorMessage(validationResult, (key, params) => this.translocoService.translate(key, params));
    
    var event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('upload.uploadCenter.headerValidationFailedTitle'),
      message: message,
      primaryBtnClass: 'blue',
      showCancel: false,
      primaryBtnText: Modal.OK
    };
    const modalRef = this.modal.open(ConfirmationModalComponent, { data: event ? event : undefined });
    modalRef.afterClosed().subscribe();
  }
}
